import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { api } from '../utils/services/api';
import { SERVICE } from '../utils/constants/services';
import styles from './communicationsModal.module.css';

export default function CommunicationsModal({ show, setShow, onSuccess }) {
    const [message, setMessage] = useState('');
    const [date, setDate] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (show) {
            handleLoadUsers();
            // Set today's date as default
            const today = new Date().toISOString().split('T')[0];
            setDate(today);
        }
    }, [show]);

    const handleLoadUsers = () => {
        api(SERVICE.getUser, {}, 'GET')
            .then((data) => {
                if (data.data.data) {
                    const usersOptions = data.data.data.map(user => ({
                        value: user.id,
                        label: `${user.firstName} ${user.lastName}`,
                        id: user.id
                    }));
                    setUsers(usersOptions);
                }
            })
            .catch((err) => {
                console.log('Error loading users:', err);
                Swal.fire('Error', 'No se pudieron cargar los usuarios', 'error');
            });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!message.trim()) {
            Swal.fire('Validación', 'Por favor ingrese un comunicado', 'warning');
            return;
        }

        if (!date) {
            Swal.fire('Validación', 'Por favor seleccione una fecha', 'warning');
            return;
        }

        setLoading(true);

        try {
            // First, create the main message
            const messagePayload = {
                Message: message,
                Date: date
            };

            const messageResponse = await api(SERVICE.postCommunication, messagePayload, 'POST');

            if (messageResponse.data.status && messageResponse.data.data) {
                const communicationId = messageResponse.data.data;

                // If users are selected, share the message with them
                if (selectedUsers && selectedUsers.length > 0) {
                    for (let i = 0; i < selectedUsers.length; i++) {
                        const userId = selectedUsers[i].value;
                        const sharedPayload = {
                            CommunicationId: communicationId,
                            UserIdShared: userId,
                            Readed: false,
                            Date: date
                        };

                        try {
                            await api(SERVICE.postCommunicationShared, sharedPayload, 'POST');
                        } catch (sharedErr) {
                            console.log('Error sharing message with user:', sharedErr);
                        }
                    }
                }

                Swal.fire('Éxito', 'Comunicado enviado correctamente', 'success');
                handleClose();
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                Swal.fire('Error', messageResponse.data.generalMessage || 'Error al enviar el comunicado', 'error');
            }
        } catch (error) {
            console.log('Error:', error);
            Swal.fire('Error', 'Error al enviar el comunicado', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setMessage('');
        setDate('');
        setSelectedUsers([]);
        setShow(false);
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton className={styles.modalHeader}>
                <Modal.Title>Nuevo Comunicado</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.modalBody}>
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Comunicado</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder="Ingrese el comunicado"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            disabled={loading}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Fecha</Form.Label>
                        <Form.Control
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            disabled={loading}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Compartir con usuarios (Opcional)</Form.Label>
                        <Select
                            isMulti
                            options={users}
                            value={selectedUsers}
                            onChange={setSelectedUsers}
                            placeholder="Seleccione usuarios..."
                            isDisabled={loading}
                            classNamePrefix="react-select"
                            styles={{
                                control: (provided) => ({
                                    ...provided,
                                    borderColor: '#ddd',
                                    '&:hover': {
                                        borderColor: '#4293D7'
                                    },
                                    '&:focus-within': {
                                        borderColor: '#4293D7',
                                        boxShadow: '0 0 0 1px #4293D7'
                                    }
                                }),
                                option: (provided, state) => ({
                                    ...provided,
                                    backgroundColor: state.isSelected ? '#4293D7' : state.isFocused ? '#e8f1f8' : 'white',
                                    color: state.isSelected ? 'white' : '#333',
                                    cursor: 'pointer'
                                })
                            }}
                        />
                    </Form.Group>

                    <div className={styles.formActions}>
                        <Button
                            variant="secondary"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? 'Enviando...' : 'Enviar'}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}
